<template>
  <div>
    <!-- Wrestler Header -->
    <div class="flex align-items-center gap-x-sm m-b-md">
      <div v-if="wrestler.avatar_thumbnail" class="flex-shrink-0">
        <img
          :src="wrestler.avatar_thumbnail"
          class="rounded-full"
          style="width: 40px; height: 40px; object-fit: cover"
          alt="Wrestler photo"
        />
      </div>
      <div
        v-else
        class="flex-shrink-0 bg-gray-100 rounded-full flex align-items-center justify-center"
        style="width: 40px; height: 40px"
      >
        <span class="text-gray-500 text-14 font-semi">{{ initials }}</span>
      </div>
      <div>
        <h3 class="m-t-none m-b-none text-16 font-semi">
          {{ wrestler.full_name }}
        </h3>
        <p class="m-b-none text-12 text-gray-500">{{ wrestlerInfo }}</p>
      </div>
    </div>

    <!-- Division Selection - Step 1 -->
    <div v-if="!selectedDivision">
      <label class="text-14 font-semi m-b-xs">Select Division</label>
      <div class="grid sm-grid-cols-1 gap-y-xs">
        <div
          v-for="division in enabledDivisions"
          :key="division.id"
          @click="selectDivision(division)"
          class="border border-solid rounded-xs p-sm cursor-pointer"
          :class="divisionCardClasses(division)"
        >
          <div class="flex justify-space-between align-items-start">
            <div>
              <p class="m-b-none text-14">
                <span class="font-semi">{{ division.display_name }}</span>
              </p>
              <p class="m-b-none text-12 text-gray-500">
                {{ division.eligibility_summary }}
              </p>
              <p class="m-b-none text-red-700 opacity-100" v-if="!isEligible(division)">
                {{ getIneligibilityText(division) }}
              </p>
            </div>
            <div v-if="isEligible(division)">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-4 w-4 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weight Class Selection - Step 2 -->
    <div v-else>
      <div class="flex align-items-center gap-x-xs m-b-sm">
        <a
          @click="clearDivision"
          class="text-12 flex align-items-center gap-x-xxs cursor-pointer"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Change division
        </a>
      </div>
      <div class="bg-gray-50 rounded-xs p-sm m-b-sm">
        <p class="m-b-none text-14 font-semi">
          {{ selectedDivision.display_name }}
        </p>
        <p class="m-b-none text-12 text-gray-500">
          {{ selectedDivision.eligibility_summary }}
        </p>
      </div>

      <label class="text-14 font-semi m-b-xs">Select Weight Class</label>
      <div class="grid grid-cols-4 sm-grid-cols-3 gap-xs">
        <div
          v-for="weightClass in selectedDivision.weight_classes"
          :key="weightClass.id"
          @click="selectWeightClass(weightClass)"
          class="border border-solid rounded-xs p-sm text-center cursor-pointer"
          :class="weightClassCardClasses(weightClass)"
        >
          <p
            class="m-b-none text-14"
            :class="selectedWeightClassId === weightClass.id ? 'font-semi' : ''"
          >
            {{ weightClass.display_name }}
          </p>
        </div>
      </div>
    </div>

    <!-- Selection summary -->
    <div
      v-if="selectedDivision && selectedWeightClassId"
      class="m-t-md p-sm bg-green-50 rounded-xs"
    >
      <p class="m-b-none text-14 font-semi text-green-700">
        {{ selectedDivision.display_name }} - {{ selectedWeightClassName }}
      </p>
    </div>
  </div>
</template>

<script>
import {
  isEligibleForDivision,
  getIneligibilityReason,
  findClosestWeightClass,
} from '../utils/divisionEligibility.js';

export default {
  name: 'wrestler-division-card',
  props: {
    wrestler: {
      type: Object,
      required: true,
    },
    enabledDivisions: {
      type: Array,
      required: true,
    },
    competitionYear: {
      type: Number,
      required: true,
    },
    selectedAgeDivisionId: {
      type: Number,
      default: null,
    },
    selectedWeightClassId: {
      type: Number,
      default: null,
    },
    brandColor: {
      type: String,
      default: '#27AA83',
    },
  },
  computed: {
    initials() {
      const first = this.wrestler.first_name?.[0] || '';
      const last = this.wrestler.last_name?.[0] || '';
      return `${first}${last}`.toUpperCase();
    },
    wrestlerInfo() {
      const parts = [];
      if (this.wrestler.age) {
        parts.push(`Age ${this.wrestler.age}`);
      }
      if (this.wrestler.academic_class) {
        parts.push(this.wrestler.academic_class);
      }
      if (this.wrestler.weight_class) {
        parts.push(`${this.wrestler.weight_class} lbs`);
      }
      return parts.join(' | ');
    },
    selectedDivision() {
      if (!this.selectedAgeDivisionId) {
        return null;
      }
      return this.enabledDivisions.find(
        (d) => d.id === Number(this.selectedAgeDivisionId),
      );
    },
    selectedWeightClassName() {
      if (!this.selectedDivision || !this.selectedWeightClassId) {
        return '';
      }
      const wc = this.selectedDivision.weight_classes.find(
        (w) => w.id === this.selectedWeightClassId,
      );
      return wc ? wc.display_name : '';
    },
    suggestedWeightClass() {
      if (!this.selectedDivision || !this.wrestler.weight_class) {
        return null;
      }
      return findClosestWeightClass(
        this.selectedDivision.weight_classes,
        this.wrestler.weight_class,
      );
    },
  },
  methods: {
    isEligible(division) {
      return isEligibleForDivision(
        this.wrestler,
        division,
        this.competitionYear,
      );
    },
    getIneligibilityText(division) {
      return getIneligibilityReason(
        this.wrestler,
        division,
        this.competitionYear,
      );
    },
    selectDivision(division) {
      if (!this.isEligible(division)) {
        return;
      }
      // Only emit division change - weight class reset is handled by parent's updateAgeDivision
      this.$emit('update:selected-age-division-id', division.id);
    },
    clearDivision() {
      // Only emit division change - weight class reset is handled by parent's updateAgeDivision
      this.$emit('update:selected-age-division-id', null);
    },
    selectWeightClass(weightClass) {
      this.$emit('update:selected-weight-class-id', weightClass.id);
    },
    divisionCardClasses(division) {
      if (!this.isEligible(division)) {
        return 'border-red-50 bg-gray-50 opacity-50';
      }
      return 'border-gray-200';
    },
    weightClassCardClasses(weightClass) {
      const isSelected = this.selectedWeightClassId === weightClass.id;

      if (isSelected) {
        return 'border-2 border-green-500 bg-green-50';
      }
      return 'border-gray-200';
    },
  },
};
</script>
